import { Customer } from "../customer/customer.model";
import { Location } from "../location/location.model";
import { Company } from "../company/company.model";
import { DeliveryRateCard } from "../contracts/models/delivery-rate-card.model";
import { AuditActorSummary } from '../shared/audit/audit.models';


export enum CrmQuotationType {
    LOGISTICS = 'LOGISTICS',
    STORAGE = 'STORAGE',
    POSM = 'POSM',
    TASK = 'TASK'
}

export enum CrmQuotationJobType {
    LOGISTICS = 'LOGISTICS',
    POSM_INSTALLATION = 'POSM_INSTALLATION',
    POSM_REFURBISHMENT = 'POSM_REFURBISHMENT',
    STORAGE = 'STORAGE',
    TASK = 'TASK',
    CUSTOMS_CLEARANCE = 'CUSTOMS_CLEARANCE',
    CF_IMPORT_AIR = 'CF_IMPORT_AIR',
    CF_IMPORT_SEA = 'CF_IMPORT_SEA',
    CF_IMPORT_LAND = 'CF_IMPORT_LAND',
    CF_EXPORT = 'CF_EXPORT',
    CF_RORO = 'CF_RORO',
    CF_CONSOLIDATION = 'CF_CONSOLIDATION',
    OTHER = 'OTHER'
}

export enum CrmQuotationStatus {
    PREPARING = 'PREPARING',
    INTERNAL_REVIEW = 'INTERNAL_REVIEW',
    READY_TO_SUBMIT = 'READY_TO_SUBMIT',
    SUBMITTED = 'SUBMITTED',
    CLIENT_REVIEW = 'CLIENT_REVIEW',
    REVISION_REQUESTED = 'REVISION_REQUESTED',
    APPROVED = 'APPROVED',
    REJECTED = 'REJECTED',
    JOB = 'JOB',
    COMPLETED = 'COMPLETED'
}

export interface CrmRoleOption {
    value: string;
    label: string;
    description?: string;
    code?: string;
}

export interface CrmLead {
    id?: number;
    leadNumber?: string;
    companyName: string;
    customerNameLocal?: string;
    pocName: string;
    pocEmail: string;
    pocPhoneNumber: string;
    mobileNumber?: string;
    source: string;
    status: string;
    kamCustomerId: number;
    leadQuality: string;
    businessType?: 'B2B' | 'B2C';
    natureOfBusiness?: string;
    taxRegNumber?: string;
    crNumber?: number;
    street1?: string;
    street1Local?: string;
    city?: string;
    cityLocal?: string;
    postalCode?: string;
    countryId?: number;
    websiteURL?: string;
    converted?: boolean;
    convertedAt?: Date;
    linkedCompanyId?: number;
    linkedCustomerId?: number;
    convertedClientId?: number;
    createdAt?: Date;
    createdByCustomerId?: number;
    createdByCustomerName?: string;
    createdByActor?: AuditActorSummary;
    updatedAt?: Date;
    updatedByCustomerId?: number;
    updatedByCustomerName?: string;
    updatedByActor?: AuditActorSummary;
    salesTeamName?: string;
    roles?: string[];
    companyId: number;
}

export interface CrmProspect {
    id?: number;
    prospectNumber?: string;
    sourceLeadId?: number;
    companyName: string;
    customerNameLocal?: string;
    pocName: string;
    pocEmail: string;
    pocPhoneNumber: string;
    mobileNumber?: string;
    source: string;
    status: string;
    kamCustomerId: number;
    prospectQuality: string;
    businessType?: 'B2B' | 'B2C';
    natureOfBusiness?: string;
    taxRegNumber?: string;
    crNumber?: number;
    street1?: string;
    street1Local?: string;
    city?: string;
    cityLocal?: string;
    postalCode?: string;
    countryId?: number;
    websiteURL?: string;
    converted?: boolean;
    convertedAt?: Date;
    linkedCompanyId?: number;
    linkedCustomerId?: number;
    convertedClientId?: number;
    createdAt?: Date;
    createdByCustomerId?: number;
    createdByCustomerName?: string;
    createdByActor?: AuditActorSummary;
    updatedAt?: Date;
    updatedByCustomerId?: number;
    updatedByCustomerName?: string;
    updatedByActor?: AuditActorSummary;
    salesTeamName?: string;
    roles?: string[];
    companyId: number;
}

export interface CrmClient {
    id?: number;
    clientNumber?: string;
    companyName: string;
    logoUrl?: string;
    customerNameLocal?: string;
    notifyingPersonName?: string;
    postingDate?: Date | string;
    currencyCode?: string;
    exchangeRate?: number;
    creditLimit?: number;
    creditPeriodDays?: number;
    defaultPaymentTermId?: number;
    creditCheckMode?: 'ENFORCE' | 'WARN' | 'DISABLED';
    creditHold?: boolean;
    creditHoldReason?: string;
    overdueToleranceDays?: number;
    businessType?: 'B2B' | 'B2C';
    natureOfBusiness?: string;
    taxRegNumber?: string;
    crNumber?: number;
    crExpiryDate?: Date | string;
    contractExpiryDate?: Date | string;
    remarks?: string;
    primaryEmail?: string;
    phoneNumber?: string;
    mobileNumber?: string;
    postalCode?: string;
    countryId?: number;
    category: string;
    website: string;
    source: string;
    kamCustomerId: number;
    linkedCompanyId?: number;
    linkedCustomerId?: number;
    linkedTransportationAgencyId?: number;
    linkedTransportationAgencyName?: string;
    sourceLeadId?: number;
    approvalStatus?: 'DRAFT' | 'APPROVED';
    approvedByCustomerId?: number;
    approvedByCustomerName?: string;
    approvedAt?: Date | string;
    createdAt?: Date;
    createdByCustomerId?: number;
    createdByCustomerName?: string;
    createdByActor?: AuditActorSummary;
    updatedAt?: Date;
    updatedByCustomerId?: number;
    updatedByCustomerName?: string;
    updatedByActor?: AuditActorSummary;
    salesTeamName?: string;
    roles?: string[];
    companyId: number;
}

export interface CrmClientQuickCreateRequest {
    client: CrmClient;
    address: CrmClientAddress;
}

export interface CrmClientQuickContactRequest {
    accountId?: number;
    name: string;
    email?: string;
    phone?: string;
    mobile?: string;
}

export interface CrmClientCreditSnapshot {
    creditStatus?: string;
    creditLimit?: number;
    creditPeriodDays?: number;
    orderPaymentTermDays?: number;
    defaultPaymentTermId?: number;
    creditCheckMode?: 'ENFORCE' | 'WARN' | 'DISABLED';
    creditHold?: boolean;
    creditHoldReason?: string;
    overdueToleranceDays?: number;
    openARAmount?: number;
    overdueARAmount?: number;
    availableCredit?: number;
    creditOverrideId?: number;
    currencyCode?: string;
    linkedCustomerId?: number;
    warnings?: string[];
    blockers?: string[];
}

export interface CrmClientAddress {
    id?: number;
    clientId?: number;
    companyId?: number;
    street1?: string;
    street1Local?: string;
    street2?: string;
    buildingNumber?: string;
    plotIdentification?: string;
    postalCode?: string;
    city?: string;
    cityId?: number | null;
    cityLocal?: string;
    countryId?: number;
}

export interface CrmClientCommunication {
    id?: number;
    clientId?: number;
    companyId?: number;
    customerId?: number;
    email?: string;
    communicationType?: string;
    name?: string;
    phone?: string;
    mobile?: string;
    extension?: string;
    fax?: string;
    website?: string;
    primaryRecord?: boolean;
}

export interface CrmClientContactAccount {
    id?: number;
    name?: string;
}

export interface CrmClientContactDirectoryEntry {
    crmClientContactId?: number;
    customerId?: number;
    name?: string;
    email?: string;
    phone?: string;
    mobile?: string;
    primaryRecord?: boolean;
    clientContact?: boolean;
    linkedCompanyContact?: boolean;
    accounts?: CrmClientContactAccount[];
}

export interface CrmClientContactResolveRequest {
    customerId: number;
    accountId?: number | null;
}

export interface CrmClientDocument {
    id?: number;
    clientId?: number;
    companyId?: number;
    title?: string;
    category?: string;
    fileName?: string;
    fileUrl?: string;
    fileType?: string;
    fileSize?: number;
    expiryDate?: Date | string;
    uploadedAt?: Date;
    uploadedByCustomerId?: number;
    uploadedByCustomerName?: string;
}

export interface CrmAccount {
    id?: number;
    companyName: string;
    logoUrl?: string;
    category: string;
    source: string;
    kamCustomerId?: number;
    clientAccountManagerCustomerIds?: number[];
    crmClientId?: number;
    createdAt?: Date;
    companyId: number;
}

export interface CrmMerchandiser {
    id?: number;
    merchandiserNumber?: string;
    name: string;
    employeeCode?: string;
    emailAddress?: string;
    phoneNumber?: string;
    mobileNumber?: string;
    region?: string;
    notes?: string;
    active?: boolean;
    createdAt?: Date | string;
    updatedAt?: Date | string;
    companyId: number;
}

export interface CrmLeadConversionRequest {
    company: import('../company/company.model').Company;
    customer: Omit<import('../customer/customer.model').Customer, 'roles'>;
    reuseCompany?: boolean;
    reuseCustomer?: boolean;
    kamPartyId?: number | null;
    clientCategory?: string;
    customerNameLocal?: string | null;
    postingDate?: Date | string | null;
    currencyCode?: string | null;
    exchangeRate?: number | null;
    businessType?: string | null;
    natureOfBusiness?: string | null;
    roles?: string[];
}

export interface CrmLeadConversionResult {
    lead: CrmLead;
    client: CrmClient;
    company: import('../company/company.model').Company;
    customer: import('../customer/customer.model').Customer;
}

export interface CrmQuotationLine {
    id?: number;
    quotationId?: number;
    lineNumber?: number;
    productId?: number;
    variantId?: number;
    productName?: string;
    description: string;
    uom?: string;
    quantity: number;
    unitPrice: number;
    taxRate?: number;
    unitTaxAmount?: number;
    unitDiscount?: number;
    totalLineAmount: number;
    itemCategory: 'PRODUCT' | 'SERVICE' | 'EXTERNAL';
}

export interface CrmQuotationPosmDetails {
    id?: number;
    quotationId?: number;
    projectName: string;
    client?: Company;
    brandName?: string;
    jobPriority?: string;
    jobType?: string;
    regionId?: number;
    warehouse?: Location;
    startDate?: Date;
    deadline?: Date;
    executionStrategy?: string;
    supplier?: Company;
    installationDate?: Date;
    removalDate?: Date;
    eventLocation?: string;
    manager?: Customer;
}

export interface CrmQuotationLogisticsDetails {
    id?: number;
    quotationId?: number;
    companyId?: number;
    duration?: string;
    workingHours?: string;
    scope?: string;
    quantity?: number;
    exclusions?: string;
    serviceScope?: string;
    shipmentMode?: string;
    importExportType?: string;
    blAwbNumber?: string;
    hblHawbNumber?: string;
    vesselOrFlight?: string;
    carrier?: string;
    originPort?: string;
    destinationPort?: string;
    etd?: Date | string;
    eta?: Date | string;
    deliveryOrderNumber?: string;
    bayanNumber?: string;
    undertakingNumber?: string;
    cargoDescription?: string;
    deliveryAddress?: string;
    clearanceRequired?: boolean;
    transportRequired?: boolean;
    containerTrackingRequired?: boolean;
    deliveryRateCard?: DeliveryRateCard;
    deliveryRateCards?: DeliveryRateCard[];
}

export interface CrmQuotationPosmInstallationDetails {
    quotationId?: number;
    companyId?: number;
    rows?: CrmQuotationPosmInstallationRow[];
}

export interface CrmQuotationPosmInstallationRow {
    id?: number;
    lineNumber?: number;
    description?: string;
    dimensions?: string;
    weight?: number;
    quantity?: number;
    installationCost?: number;
    remarks?: string;
}

export interface CrmQuotationStorageDetails {
    quotationId?: number;
    companyId?: number;
    rows?: CrmQuotationStorageChargeRow[];
}

export interface CrmQuotationStorageChargeRow {
    id?: number;
    lineNumber?: number;
    chargeType?: string;
    description?: string;
    metric?: string;
    rate?: number;
    quantity?: number;
    amount?: number;
    regionId?: number;
    warehouseId?: number;
    remarks?: string;
}

export interface CrmQuotation {
    id?: number;
    quotationNumber?: string;
    subject: string;
    description?: string;
    countryId?: number | null;
    cityId?: number | null;
    cityName?: string;
    customerName: string;
    customerId?: number;
    contactCustomer?: Customer;
    pocName?: string;
    pocEmail?: string;
    type: CrmQuotationType;
    jobType?: CrmQuotationJobType;
    status: CrmQuotationStatus;
    startDate?: Date;
    expiryDate?: Date;
    currencyCode?: string;
    exchangeRate?: number;
    vatRate?: number;
    discountType?: 'AMOUNT' | 'PERCENT';
    pricingNotes?: string;
    termsHtml?: string;
    paymentTerms?: string;
    approvalRequired?: boolean;
    deviationStatus?: 'NO_CRITERIA' | 'WITHIN_CRITERIA' | 'DEVIATION' | string;
    deviationSummary?: string;
    pocPhoneNumber?: string;
    pocMobileNumber?: string;
    subTotal?: number;
    discountAmount?: number;
    vatAmount?: number;
    totalAmount: number;
    kamCustomerId: number;
    leadId?: number;
    prospectId?: number;
    clientId?: number;
    approvedByUserId?: number;
    approvedAt?: Date;
    rejectedByUserId?: number;
    rejectedAt?: Date;
    rejectionReason?: string;
    isEmailed?: boolean;
    emailSentDate?: Date;
    emailOpensCount?: number;
    createdAt?: Date;
    createdByCustomerId?: number;
    createdByCustomerName?: string;
    createdByActor?: AuditActorSummary;
    updatedAt?: Date;
    updatedByCustomerId?: number;
    updatedByCustomerName?: string;
    updatedByActor?: AuditActorSummary;
    salesTeamName?: string;
    companyId: number;
    lines?: CrmQuotationLine[];
    posmDetails?: CrmQuotationPosmDetails;
    logisticsDetails?: CrmQuotationLogisticsDetails;
    posmInstallationDetails?: CrmQuotationPosmInstallationDetails;
    storageDetails?: CrmQuotationStorageDetails;
}

export interface CrmQuotationTermsTemplate {
    id?: number;
    companyId: number;
    jobType: CrmQuotationJobType;
    title?: string;
    description?: string;
    termsHtml?: string;
    active?: boolean;
}

export interface CrmQuotationClientCriteria {
    id?: number;
    companyId: number;
    clientId?: number;
    customerId?: number;
    jobType: CrmQuotationJobType;
    maxDiscountPercent?: number;
    maxAmount?: number;
    maxRateDeviationPercent?: number;
    minMarginPercent?: number;
    autoApprovalAmountLimit?: number;
    active?: boolean;
}

export interface CrmScorecards {
    [key: string]: number | string | boolean | null | undefined;
}

export interface CrmQuotationAttachment {
    id?: number;
    quotationId?: number;
    companyId?: number;
    category: 'PROPOSAL' | 'SOW' | 'PRICING' | 'OTHER';
    fileName: string;
    fileUrl: string;
    fileType?: string;
    fileSize?: number;
    visibility?: 'INTERNAL' | 'CLIENT_VISIBLE';
    uploadedAt?: Date;
    uploadedByCustomerId?: number;
    generated?: boolean;
    versionNumber?: number;
    sourceType?: string;
    generatedAt?: Date;
}

export interface CrmQuotationRequirement {
    id?: number;
    quotationId?: number;
    companyId?: number;
    sourceType?: 'LEAD' | 'PROSPECT' | 'CLIENT';
    sourceRecordId?: number;
    description: string;
    priority?: string;
    status?: string;
    createdAt?: Date;
    createdByCustomerId?: number;
    updatedAt?: Date;
    updatedByCustomerId?: number;
}

export interface CrmQuotationTask {
    id?: number;
    quotationId?: number;
    requirementId?: number;
    companyId?: number;
    title: string;
    description?: string;
    assignedTeam?: string;
    assignedCustomerId?: number;
    dueDate?: Date;
    status?: 'OPEN' | 'IN_PROGRESS' | 'BLOCKED' | 'COMPLETED' | 'NOT_REQUIRED';
    completionNotes?: string;
    requiredForSubmission?: boolean;
    createdAt?: Date;
    createdByCustomerId?: number;
    updatedAt?: Date;
    updatedByCustomerId?: number;
}

export interface CrmQuotationReviewNote {
    id?: number;
    quotationId?: number;
    requirementId?: number;
    taskId?: number;
    companyId?: number;
    visibility?: 'INTERNAL' | 'CLIENT_VISIBLE';
    stakeholderType?: 'INTERNAL' | 'CLIENT';
    externalReviewerName?: string;
    externalReviewerEmail?: string;
    noteText: string;
    createdAt?: Date;
    createdByCustomerId?: number;
}

export interface CrmQuotationExternalReview {
    id?: number;
    quotationId?: number;
    companyId?: number;
    recipientEmail: string;
    token?: string;
    expiresAt?: Date;
    status?: string;
    decision?: string;
    decisionNotes?: string;
    submittedAt?: Date;
    decidedAt?: Date;
    submittedByCustomerId?: number;
}

export interface CrmNote {
    id?: number;
    leadId?: number;
    prospectId?: number;
    clientId?: number;
    text: string;
    createdAt?: Date;
    createdByUserId?: number;
    companyId: number;
}

export interface CrmCommunicationLog {
    id?: number;
    leadId?: number;
    prospectId?: number;
    clientId?: number;
    type: string; // EMAIL, CALL, MEETING
    communicationDate: Date;
    summary: string;
    status: string;
    createdAt?: Date;
    createdByUserId?: number;
    companyId: number;
}

export interface CrmReminder {
    id?: number;
    leadId?: number;
    prospectId?: number;
    clientId?: number;
    dueDate: Date;
    reminderText: string;
    completed: boolean;
    assignedUserId: number;
    createdAt?: Date;
    createdByUserId?: number;
    companyId: number;
}

export enum CrmActivityType {
    NOTE = 'NOTE',
    COMMUNICATION = 'COMMUNICATION',
    REMINDER = 'REMINDER'
}

export interface CrmActivityItem {
    id?: number;
    activityType: CrmActivityType;
    title: string;
    summary?: string;
    status?: string;
    occurredAt?: Date;
    leadId?: number;
    prospectId?: number;
    clientId?: number;
    createdByUserId?: number;
    companyId?: number;
    completed?: boolean;
}

export interface CrmAnalyticsSummary {
    totalLeads: number;
    totalClients: number;
    totalAccounts: number;
    pipelineValue: number;
    openReminders: number;
    overdueReminders: number;
    leadConversionRate: number;
    leadsByStatus: Record<string, number>;
    leadsBySource: Record<string, number>;
    quotesByStatus: Record<string, number>;
    quotesByType: Record<string, number>;
    revenueByServiceLine: Record<string, number>;
    pipelineValueByType: Record<string, number>;
}

export interface PageResponse<T> {
    content: T[];
    totalElements: number;
    totalPages?: number;
    number?: number;
    size?: number;
}

export interface CrmDuplicatePair<T> {
    primary: T;
    duplicate: T;
    reasons: string[];
}

export interface CrmDuplicateResponse<T> {
    pairs: CrmDuplicatePair<T>[];
}

export interface CrmClientEmployeeAssignment {
    id?: number;
    companyId: number;
    clientParty: import('../party/party.model').Party;
    employeeParty: import('../party/party.model').Party;
    primaryOwner?: boolean;
    assignmentRole?: string;
    active?: boolean;
    createdAt?: Date;
    createdByCustomerId?: number;
    createdByCustomerName?: string;
    updatedAt?: Date;
    updatedByCustomerId?: number;
    updatedByCustomerName?: string;
}

export interface CrmClientEmployeeAssignmentRequest {
    companyId: number;
    clientPartyId: number;
    primaryEmployeePartyId?: number;
    employeePartyIds?: number[];
    teamEmployeePartyIds?: number[];
    assignmentRole?: string;
    active?: boolean;
    createdByCustomerId?: number;
    updatedByCustomerId?: number;
}

export interface CrmClientRegistrationAttachment {
    id?: number;
    leadId?: number;
    prospectId?: number;
    clientId?: number;
    companyId?: number;
    ownerCompanyId?: number;
    category: CrmRegistrationAttachmentCategory;
    fileName: string;
    fileUrl: string;
    fileType?: string;
    fileSize?: number;
    uploadedAt?: Date;
    uploadedByCustomerId?: number;
    uploadedByCustomerName?: string;
}

export type CrmRegistrationAttachmentCategory =
    'VAT_CERTIFICATE' |
    'COMMERCIAL_REGISTRATION_CERTIFICATE' |
    'NATIONAL_ADDRESS' |
    'CR' |
    'VAT';

export const CRM_REGISTRATION_ATTACHMENT_TYPES: { label: string; value: CrmRegistrationAttachmentCategory }[] = [
    { label: 'VAT certificate', value: 'VAT_CERTIFICATE' },
    { label: 'Commercial Registration Certificate', value: 'COMMERCIAL_REGISTRATION_CERTIFICATE' },
    { label: 'National Address', value: 'NATIONAL_ADDRESS' }
];

export const CRM_CLIENT_DOCUMENT_TYPES: { label: string; value: string }[] = [
    { label: 'General', value: 'GENERAL' },
    { label: 'VAT certificate', value: 'VAT_CERTIFICATE' },
    { label: 'Commercial Registration Certificate', value: 'COMMERCIAL_REGISTRATION_CERTIFICATE' },
    { label: 'National Address', value: 'NATIONAL_ADDRESS' },
    { label: 'Contract', value: 'CONTRACT' },
    { label: 'Verification', value: 'VERIFICATION' }
];

export interface CrmProspectConversionResult {
    prospect: CrmProspect;
    client: CrmClient;
    company: import('../company/company.model').Company;
    customer: import('../customer/customer.model').Customer;
}

export function crmRoleLabel(role?: string | null): string {
    return String(role || '')
        .trim()
        .toLowerCase()
        .split('_')
        .filter(Boolean)
        .map(part => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
}
